import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Briefcase, Heart, Car, Target, Plane, Send, CheckCircle2, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { AnimatePresence } from 'motion/react';
import Field from '../components/Field';
import { api } from '../utils/api';

const SERVICES: Record<string, {
  title: string; icon: any; color: string; tagline: string;
  description: string; highlights: string[]; image: string;
}> = {
  'mutual-funds': {
    title: 'Mutual Funds (SWP, SIP, Lumpsum)', icon: Briefcase, color: '#1d5fa8',
    tagline: 'Build long-term wealth systematically',
    description: 'Mutual Funds are one of the most accessible and effective ways to grow wealth. Whether you prefer the discipline of a Systematic Investment Plan (SIP), the flexibility of a lump-sum investment, or the steady income of a Systematic Withdrawal Plan (SWP), Stone Bench helps you select the right fund basket aligned with your goals and risk appetite.',
    highlights: ['SIP starting from ₹500/month', 'Goal-based fund selection', 'Tax-efficient ELSS options', 'Portfolio rebalancing guidance', 'Regular performance reviews'],
    image: '/./mutual_fund.webp',
  },
  'life-term-cover': {
    title: 'Life & Term Cover', icon: Heart, color: '#dc2626',
    tagline: 'Protect what matters most',
    description: "A term insurance plan is the purest form of financial protection for your family. Stone Bench guides you through choosing the right sum assured, policy tenure, and riders so your loved ones are shielded from financial hardship regardless of life's uncertainties.",
    highlights: ['High cover at low premium', 'Accidental & critical illness riders', 'Claim assistance support', 'Comparison across top insurers', 'Online & offline policy options'],
    image: '/./life_term.webp',
  },
  'health-insurance': {
    title: 'Health Insurance', icon: Heart, color: '#7c3aed',
    tagline: 'Comprehensive healthcare protection',
    description: 'Medical emergencies can deplete savings in no time. Our health insurance advisory covers individual plans, family floater policies, senior citizen plans, and top-up covers — ensuring you have adequate financial protection for hospitalisation and critical illness treatment.',
    highlights: ['Cashless hospitalisation network', 'Pre & post hospitalisation cover', 'No-claim bonus benefits', 'Maternity and newborn cover', 'Portability guidance'],
    image: '/./life_insurance.webp',
  },
  'vehicle-insurance': {
    title: 'Vehicle Insurance', icon: Car, color: '#d97706',
    tagline: 'Drive with confidence and coverage',
    description: 'From two-wheelers to commercial fleets, Stone Bench helps you secure the right vehicle insurance policy. We assist with comprehensive vs third-party coverage decisions, add-on selection (zero depreciation, engine protect), renewals, and hassle-free claim filing.',
    highlights: ['Zero depreciation add-on', 'Engine & gearbox protection', 'Roadside assistance cover', 'Instant policy renewal', 'Claim tracking support'],
    image: '/./car_insurance.webp',
  },
  'target-returns': {
    title: 'Target Returns', icon: Target, color: '#5f9c4c',
    tagline: 'Goal-based investing made precise',
    description: "Target Returns is a goal-based investment strategy where every rupee is mapped to a specific milestone — child's education, home purchase, retirement corpus, or a dream vacation. We calculate the required returns, select instruments, and create a structured plan to achieve each goal on time.",
    highlights: ['Milestone-based portfolio design', 'Inflation-adjusted projections', 'Hybrid asset allocation', 'Quarterly progress tracking', 'Rebalancing alerts'],
    image: '/./target_returns.webp',
  },
  'group-travel-insurance': {
    title: 'Group & Travel Insurance', icon: Plane, color: '#0891b2',
    tagline: 'Stay protected everywhere you go',
    description: "Whether you're an employer looking to secure your workforce or an individual planning international travel, Stone Bench offers comprehensive group health plans and travel insurance products covering medical emergencies, trip cancellations, baggage loss, and more.",
    highlights: ['Group mediclaim for employees', 'International travel coverage', 'Trip cancellation protection', 'Emergency medical evacuation', 'Baggage & document loss cover'],
    image: '/./travel_insurance.webp',
  },
};

// ── Shared query modal (same component used on Services page cards) ────────────
function QueryModal({ serviceName, serviceColor, onClose }: {
  serviceName: string; serviceColor: string; onClose: () => void;
}) {
  const { toast } = useToast();
  const { user }  = useAuth();
  const [form, setForm] = useState({
    name:    user?.name  ?? '',
    email:   user?.email ?? '',
    phone:   '',
    remarks: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const set = (k: keyof typeof form) => (v: string) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim())                                    e.name  = 'Name is required.';
    if (!form.email.trim())                                   e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email))               e.email = 'Enter a valid email.';
    if (!form.phone.trim())                                   e.phone = 'Phone is required.';
    else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit number.';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    try {
      try { await api.post('/queries', { ...form, service_type: serviceName });
      {
        toast("Query submitted! We'll contact you soon.", 'success');
        onClose();
      } } catch { toast('Submission failed. Please try again.', 'error'); }
    } catch { toast('Network error. Please try again.', 'error'); }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="card w-full max-w-md p-6 sm:p-8"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${serviceColor}18` }}>
              <Send className="w-4 h-4" style={{ color: serviceColor }} />
            </div>
            <div>
              <p className="text-sm font-black text-[var(--color-text)]">Get More Details</p>
              <p className="text-[11px] text-[var(--color-muted)] leading-tight max-w-[220px] truncate">{serviceName}</p>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label="Name" error={errors.name} required>
            <input className={`input ${errors.name ? 'border-red-400' : ''}`}
              value={form.name} onChange={e => set('name')(e.target.value)} placeholder="Your full name" />
          </Field>
          <Field label="Email Address" error={errors.email} required>
            <input type="email" className={`input ${errors.email ? 'border-red-400' : ''}`}
              value={form.email} onChange={e => set('email')(e.target.value)} placeholder="you@email.com" />
          </Field>
          <Field label="Phone" error={errors.phone} required>
            <input type="tel" className={`input ${errors.phone ? 'border-red-400' : ''}`}
              value={form.phone} onChange={e => set('phone')(e.target.value)}
              placeholder="9876543210" maxLength={10} />
          </Field>
          <Field label="Remarks / Questions">
            <textarea className="input" rows={3}
              value={form.remarks} onChange={e => set('remarks')(e.target.value)}
              placeholder="Tell us your investment goals or any questions…" />
          </Field>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading}
              className="btn btn-primary flex-1 py-3 rounded-xl text-sm disabled:opacity-60 shadow-lg shadow-[var(--color-accent)]/10">
              {loading ? 'Submitting…' : 'Submit Query'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-ghost rounded-xl px-6">Cancel</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// ── Service Detail page ───────────────────────────────────────────────────────
export default function ServiceDetail() {
  const { slug = '' } = useParams();
  const svc = SERVICES[slug];
  const [showQuery, setShowQuery] = useState(false);

  if (!svc) {
    return (
      <div className="section-py container-fluid text-center">
        <p className="text-[var(--color-muted)]">Service not found.</p>
        <Link to="/services" className="btn btn-primary mt-4">Back to Services</Link>
      </div>
    );
  }

  const Icon = svc.icon;

  return (
    <div className=" container-fluid max-w-7xl pt-10 pb-10 mx-auto space-y-12">
      {/* Back */}
      <Link to="/services" className="inline-flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors font-semibold">
        <ArrowLeft className="w-4 h-4" /> Back to Services
      </Link>

      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4"
            style={{ background: `${svc.color}15`, color: svc.color, border: `1px solid ${svc.color}30` }}>
            <Icon className="w-3.5 h-3.5" /> Investment Service
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[var(--color-text)] tracking-tight mb-3 leading-tight">{svc.title}</h1>
          <p className="text-[var(--color-muted)] text-lg mb-6 leading-relaxed">{svc.tagline}</p>
          <p className="text-[var(--color-muted)] leading-relaxed text-[15px] mb-8">{svc.description}</p>

          {/* CTA button — opens the same query modal */}
          <button
            onClick={() => setShowQuery(true)}
            className="btn btn-primary py-3 px-6 rounded-xl text-sm flex items-center gap-2"
            style={{ background: svc.color }}
          >
            <Send className="w-4 h-4" /> Get More Queries
          </button>
        </div>
        <div className="rounded-2xl overflow-hidden aspect-video md:aspect-[4/5]">
          <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
        </div>
      </motion.div>

      {/* Highlights */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="rounded-2xl p-6 sm:p-8" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
        <h2 className="text-lg font-black text-[var(--color-text)] mb-5">What&apos;s Included</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {svc.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: svc.color }} />
              <span className="text-sm text-[var(--color-text)]">{h}</span>
            </div>
          ))}
        </div>        
      </motion.div>

      {/* Query modal */}
      <AnimatePresence>
        {showQuery && (
          <QueryModal
            serviceName={svc.title}
            serviceColor={svc.color}
            onClose={() => setShowQuery(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
